/* eslint-disable jira/js/team-name-enforcer-for-error-analytics */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { createLocalStorageProvider } from '@atlassian/jira-browser-storage-providers';
import { setUserProperties } from '@atlassian/jira-common-rest/src/api/latest/user-properties';
import { useService } from '@atlassian/jira-common-services/src/use-service';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { performGetRequest } from '@atlassian/jira-fetch';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import { lastSevenPredunningDaysLeft } from '../../common/utils';
import type { Response } from './types';

const oneDay = 1000 * 3600 * 24;
export const LAST_SEEN_TIME_PROPERTY_NAME = 'jira-eot-lock-screen-modal.last-seen-time';

export const storage = createLocalStorageProvider('jsw-onboarding-premium-eot-lock-screen');

export const fetchModalLastSeenTime = async () => {
	// @todo: Haven't managed to find a frontendconfig query in AGG. If it existed we could convert to Relay
	const { user }: Response = await performGetRequest(
		`/rest/internal/latest/frontendconfig/properties?user=${LAST_SEEN_TIME_PROPERTY_NAME}`,
	);
	return user[LAST_SEEN_TIME_PROPERTY_NAME];
};

export const getModalLastSeenTimeWithCache = async () => {
	const cachedLastTimeSeen = storage.get(LAST_SEEN_TIME_PROPERTY_NAME);

	if (cachedLastTimeSeen) {
		return cachedLastTimeSeen;
	}

	const lastTimeSeen = await fetchModalLastSeenTime();

	if (lastTimeSeen) {
		storage.set(LAST_SEEN_TIME_PROPERTY_NAME, lastTimeSeen);
	}

	return lastTimeSeen;
};

export const updateModalLastSeenTime = async (userAccountId: string, date: string) => {
	try {
		storage.set(LAST_SEEN_TIME_PROPERTY_NAME, date);

		await setUserProperties('', userAccountId, LAST_SEEN_TIME_PROPERTY_NAME, date);
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireErrorAnalytics({
			error,
			meta: {
				id: 'updateModalLastSeenTime',
				packageName: 'jiraLockScreenExperiment',
				teamName: 'jlove-makkuro',
			},
			sendToPrivacyUnsafeSplunk: true,
		});
	}
};

export const useShouldShowJiraLockScreen = ({
	predunningDaysLeft,
}: {
	predunningDaysLeft: number | undefined;
}): boolean => {
	const isFetched = useRef<boolean>(false);
	const { atlassianAccountId } = useTenantContext();
	const isInLastSevenPredunningDays = lastSevenPredunningDaysLeft(predunningDaysLeft);

	const request = useCallback(async () => {
		try {
			if (!atlassianAccountId) {
				fireErrorAnalytics({
					error: new Error('No atlassian account id'),
					meta: {
						id: 'fetchModalLastSeenTime',
						packageName: 'jiraLockScreenExperiment',
						teamName: 'jlove-makkuro',
					},
					sendToPrivacyUnsafeSplunk: true,
				});
				return Date.now(); // if no account id provided, consider as no screen shown
			}

			const result = await getModalLastSeenTimeWithCache();
			return result || null;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				error,
				meta: {
					id: 'fetchModalLastSeenTime',
					packageName: 'jiraLockScreenExperiment',
					teamName: 'jlove-makkuro',
				},
				sendToPrivacyUnsafeSplunk: true,
			});
			return undefined;
		}
	}, [atlassianAccountId]);

	const { loading, data, error, fetch } = useService(request);

	useEffect(() => {
		if (data !== undefined || loading || error || isFetched.current) {
			return;
		}

		// modal is always shown if user is in last 7 days of predunning
		// so no need to check the last seen time
		if (isInLastSevenPredunningDays) {
			return;
		}

		isFetched.current = true;

		fetch();
	}, [data, error, fetch, isInLastSevenPredunningDays, loading]);

	const shouldShowJiraLockScreen = useMemo(() => {
		// No need to wait for an API response as we always show the modal in the last 7 days of predunning
		if (isInLastSevenPredunningDays) {
			return true;
		}

		// Wait for the local storage cache or API response to determine if we should show the modal
		if (data === undefined || loading || error) {
			return false;
		}

		// If user has not seen the modal before we should show it
		if (data === null) {
			return true;
		}

		// When the user has seen the modal before, we should show it again if they haven't seen it in the last 24 hours
		const now = Date.now();
		const lastSeenTime = Number(data);
		const hasNotSeenLockScreenInLastDay = now - lastSeenTime > oneDay;

		return hasNotSeenLockScreenInLastDay;
	}, [data, error, isInLastSevenPredunningDays, loading]);

	return shouldShowJiraLockScreen;
};

export const useUpdateLastSeenOnDismiss = () => {
	const { atlassianAccountId } = useTenantContext();
	const updateLastSeen = useCallback(() => {
		const now = Date.now();
		updateModalLastSeenTime(atlassianAccountId || '', `${now}`);
	}, [atlassianAccountId]);
	return updateLastSeen;
};
